import {
  getAgentReferralFromParams,
  getMarketplaceClaimFromParams,
} from "../../lib/onboarding";
import type { Posture } from "./types";

export type AgentReferral = ReturnType<typeof getAgentReferralFromParams>;

export interface PostureFlags {
  /** `marketplace_funnel_enabled` PostHog flag, resolved by the caller. */
  funnelEnabled: boolean;
  /**
   * `invited_funnel_enabled` PostHog flag, resolved by the caller. Optional
   * so every existing `{funnelEnabled: ...}` call site (tests, App.tsx)
   * stays valid — an absent/falsy flag routes referrals to `legacy`,
   * byte-identical to today's behavior.
   */
  invitedFunnelEnabled?: boolean;
}

/**
 * Resolves which onboarding experience a visitor gets (tech spec §"Frontend"
 * D-T1; test spec "posture resolution matrix"). Priority order, top wins:
 *
 * 1. Referral/invite present → `invited` when `invitedFunnelEnabled` is on,
 *    else `legacy`. An agent-invited buyer already has an agent, so they are
 *    never routed into the marketplace funnel — but with the invited flag on
 *    they get the tailored `invited` funnel variant (posture-configs.ts)
 *    instead of the current onboarding. This check is independent of
 *    `funnelEnabled` (the marketplace flag): a referral's posture is decided
 *    solely by `invitedFunnelEnabled`. Referral attribution itself is
 *    unaffected — it still applies via the pre-existing
 *    `getAgentReferralFromParams` path regardless of which posture is chosen.
 * 2. Marketplace claim token present → `legacy`. A claim is never treated as
 *    an agent referral (see rule 1), so it falls through to this check.
 *
 *    THIS RULE IS LOAD-BEARING, not a leftover — do not "reconcile" it with
 *    the flags without doing the work first. Only `OnboardingFlow` reads the
 *    claim token off the URL and persists it into the onboarding session
 *    (`marketplaceClaimToken`), which `OnboardingGuard` then reads to build
 *    the post-signup resume path. The funnel has NO claim handling at all, so
 *    routing a claim into it silently drops the token: the buyer completes
 *    onboarding and never lands on the offer they came to accept. The same
 *    holds for `marketplace_intent=publish` and `referral_token`, which
 *    `OnboardingFlow` also carries. Retiring this rule means teaching the
 *    funnel to carry those three first.
 * 3. `funnelEnabled` → `marketplace`. The flag is the master switch for the
 *    whole funnel feature; when ON, ORGANIC (non-referred) buyers get the
 *    funnel; when OFF, everyone gets the current onboarding.
 * 4. Otherwise → `legacy` (OnboardingGuard keeps mounting the existing
 *    AnonOnboardingPage/OnboardingPage split; the chat-door A/B stays live).
 *
 * DELIBERATELY BLIND to the Ask Sill `?q=` seed. A seeded visitor still gets
 * the funnel — the seed only decides whether the seed-answer entry
 * (`AnonOnboardingPage`, which answers the question once and then hands off)
 * renders in FRONT of it. That is an entry-surface question, not a posture
 * question, and it lives in OnboardingGuard. An earlier revision routed `?q=`
 * to `legacy` here as a stopgap; folding the answer into the funnel path
 * replaced it, so a seeded visitor is no longer excluded from the funnel.
 *
 * `searchParams` carries the claim token; callers resolve `referral` from the
 * same params via `getAgentReferralFromParams` upstream and pass it in
 * separately.
 */
export function resolvePosture(
  searchParams: URLSearchParams,
  referral: AgentReferral,
  flags: PostureFlags,
): Posture {
  // Referrals route to the invited funnel when the flag is on; otherwise
  // they fall back to current onboarding — never the marketplace funnel.
  if (referral) return flags.invitedFunnelEnabled ? "invited" : "legacy";
  // A marketplace claim token is never an agent referral — legacy onboarding.
  if (getMarketplaceClaimFromParams(searchParams)) return "legacy";
  // Flag is the master switch; ON → organic gets the funnel.
  return flags.funnelEnabled ? "marketplace" : "legacy";
}

/**
 * True whenever the funnel mounts at all — used to decide whether the
 * chat-door A/B (AnonOnboardingPage) should be suppressed (test spec "chat
 * door A/B suppressed only when funnel on").
 */
export function isFunnelPosture(posture: Posture): boolean {
  return posture !== "legacy";
}
